import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Switch,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CloudIcon from '@mui/icons-material/Cloud';
import InfoIcon from '@mui/icons-material/Info';
import { IconRefresh, IconPlus } from '@tabler/icons-react';
import { SecondaryToolbar, ToolbarSpacer, ToolbarButton } from '../components/toolbar';
import {
  DataTableCard,
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
} from '../components/table';
import { Chip } from '../components/chip';
import type {
  Process,
  ScheduledFunction,
  CreateScheduledFunctionRequest,
  UpdateScheduledFunctionRequest,
} from '@techgitter/contracts';
import {
  getScheduledFunctions,
  createScheduledFunction,
  updateScheduledFunction,
  deleteScheduledFunction,
  runScheduledFunction,
} from '../services/scheduledFunctionService';
import { getProcesses } from '../services/processesApi';
import { ScheduledFunctionDialog } from '../components/admin/ScheduledFunctionDialog';

function formatRelativeTime(isoString: string): string {
  const now = Date.now();
  const then = new Date(isoString).getTime();
  const diffMs = now - then;

  if (diffMs < 0) return 'just now';

  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatSchedule(fn: ScheduledFunction): string {
  const label = fn.scheduleType === 'daily' ? 'Daily' : 'Weekdays';
  return `${label} at ${fn.scheduledTime}`;
}

function getNextScheduledRun(fns: ScheduledFunction[]): { name: string; when: string } | null {
  const enabled = fns.filter((f) => f.isEnabled);
  if (enabled.length === 0) return null;

  const now = new Date();
  let nearest: Date | null = null;
  let nearestFn: ScheduledFunction | null = null;

  for (const fn of enabled) {
    const [hours, minutes] = fn.scheduledTime.split(':').map(Number);

    const candidate = new Date(now);
    candidate.setHours(hours, minutes, 0, 0);

    if (candidate <= now) {
      candidate.setDate(candidate.getDate() + 1);
    }

    if (fn.scheduleType === 'weekday') {
      while (candidate.getDay() === 0 || candidate.getDay() === 6) {
        candidate.setDate(candidate.getDate() + 1);
      }
    }

    if (!nearest || candidate < nearest) {
      nearest = candidate;
      nearestFn = fn;
    }
  }

  if (!nearest || !nearestFn) return null;

  const isToday = nearest.toDateString() === now.toDateString();
  const timeStr = nearest.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  let when: string;
  if (isToday) {
    when = `today at ${timeStr}`;
  } else {
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (nearest.toDateString() === tomorrow.toDateString()) {
      when = `tomorrow at ${timeStr}`;
    } else {
      when = `${nearest.toLocaleDateString([], { weekday: 'long' })} at ${timeStr}`;
    }
  }

  return { name: nearestFn.name, when };
}

export const SchedulerAdminPage: React.FC = () => {
  const [functions, setFunctions] = useState<ScheduledFunction[]>([]);
  const [processes, setProcesses] = useState<Process[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFunction, setEditingFunction] = useState<ScheduledFunction | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [runningId, setRunningId] = useState<string | null>(null);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const loadFunctions = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getScheduledFunctions(signal);
      setFunctions(data);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Failed to load scheduled functions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadFunctions(controller.signal);
    // Processes drive the dialog dropdown + per-row shortcut links. A failure
    // here shouldn't block the page — the dropdown just shows no options.
    getProcesses(controller.signal)
      .then(setProcesses)
      .catch((err) => {
        if (err instanceof Error && err.name === 'AbortError') return;
      });
    return () => controller.abort();
  }, [loadFunctions]);

  const handleCreate = () => {
    setEditingFunction(null);
    setDialogError(null);
    setDialogOpen(true);
  };

  const handleEdit = (fn: ScheduledFunction) => {
    setEditingFunction(fn);
    setDialogError(null);
    setDialogOpen(true);
  };

  const handleSave = async (payload: CreateScheduledFunctionRequest | UpdateScheduledFunctionRequest) => {
    setIsSaving(true);
    setDialogError(null);

    try {
      if (editingFunction) {
        await updateScheduledFunction(editingFunction.id, payload);
      } else {
        await createScheduledFunction(payload as CreateScheduledFunctionRequest);
      }
      setDialogOpen(false);
      setEditingFunction(null);
      setSnackbar({
        open: true,
        message: editingFunction ? 'Function updated successfully' : 'Function created successfully',
        severity: 'success',
      });
      loadFunctions();
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleEnabled = async (fn: ScheduledFunction) => {
    const previousState = fn.isEnabled;
    setFunctions((current) =>
      current.map((f) => (f.id === fn.id ? { ...f, isEnabled: !f.isEnabled } : f))
    );

    try {
      await updateScheduledFunction(fn.id, { isEnabled: !previousState });
    } catch (err) {
      setFunctions((current) =>
        current.map((f) => (f.id === fn.id ? { ...f, isEnabled: previousState } : f))
      );
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'Failed to update',
        severity: 'error',
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId) return;

    setIsDeleting(true);
    try {
      await deleteScheduledFunction(deleteConfirmId);
      setFunctions((current) => current.filter((f) => f.id !== deleteConfirmId));
      setDeleteConfirmId(null);
      setSnackbar({ open: true, message: 'Function deleted', severity: 'success' });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'Failed to delete',
        severity: 'error',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRun = async (fn: ScheduledFunction) => {
    setRunningId(fn.id);
    try {
      const result = await runScheduledFunction(fn.id);
      setSnackbar({
        open: true,
        message: result.status === 'triggered'
          ? `${fn.name} triggered successfully`
          : `${fn.name} failed: ${result.message ?? 'Unknown error'}`,
        severity: result.status === 'triggered' ? 'success' : 'error',
      });
      loadFunctions();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'Failed to trigger function',
        severity: 'error',
      });
    } finally {
      setRunningId(null);
    }
  };

  const deleteTarget = functions.find((f) => f.id === deleteConfirmId);
  const processById = new Map(processes.map((p) => [p.id, p]));

  return (
    <>
      <SecondaryToolbar floating>
          {!loading && (() => {
            const now = new Date();
            const ms = now.getTime();
            const nextCheckMs = Math.ceil(ms / 300000) * 300000;
            const nextCheck = new Date(nextCheckMs);
            const nextCheckStr = nextCheck.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

            const nextRun = getNextScheduledRun(functions);
            return (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Next scheduler check: {nextCheckStr}
                </Typography>
                {nextRun && (
                  <Typography variant="caption" color="text.secondary">
                    &middot; Next function: {nextRun.name} ({nextRun.when})
                  </Typography>
                )}
              </Box>
            );
          })()}
          <ToolbarSpacer />
          <ToolbarButton icon={IconRefresh} onClick={() => loadFunctions()}>
            Refresh
          </ToolbarButton>
          <ToolbarButton variant="primary" icon={IconPlus} onClick={handleCreate}>
            Add Function
          </ToolbarButton>
      </SecondaryToolbar>

      <Box sx={{ p: '12px 16px 24px' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataTableCard>
          <DataTable>
            <thead>
              <tr>
                <DataTableHeadCell>Name</DataTableHeadCell>
                <DataTableHeadCell>Description</DataTableHeadCell>
                <DataTableHeadCell>URL</DataTableHeadCell>
                <DataTableHeadCell>Schedule</DataTableHeadCell>
                <DataTableHeadCell>Process</DataTableHeadCell>
                <DataTableHeadCell>Last Run</DataTableHeadCell>
                <DataTableHeadCell>Enabled</DataTableHeadCell>
                <DataTableHeadCell align="right">Actions</DataTableHeadCell>
              </tr>
            </thead>
            <tbody>
              {functions.length === 0 ? (
                <DataTableEmpty colSpan={8}>No scheduled functions configured</DataTableEmpty>
              ) : (
                functions.map((fn) => (
                  <DataTableRow key={fn.id}>
                    <DataTableCell bold>{fn.name}</DataTableCell>
                    <DataTableCell>{fn.description || '—'}</DataTableCell>
                    <DataTableCell>
                      <Tooltip title={fn.url}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 250, fontSize: 13 }}>
                          <strong>{fn.httpMethod}</strong> {fn.url}
                        </Typography>
                      </Tooltip>
                    </DataTableCell>
                    <DataTableCell>{formatSchedule(fn)}</DataTableCell>
                    <DataTableCell>
                      {fn.processId ? (() => {
                        const proc = processById.get(fn.processId);
                        const label = proc?.name ?? fn.processName ?? 'Process';
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Chip>{label}</Chip>
                            {proc?.urlAzure && (
                              <Tooltip title="Open process in Azure">
                                <IconButton size="small" component="a" href={proc.urlAzure} target="_blank" rel="noopener noreferrer">
                                  <CloudIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {proc?.urlInfo && (
                              <Tooltip title="Open process info / docs">
                                <IconButton size="small" component="a" href={proc.urlInfo} target="_blank" rel="noopener noreferrer">
                                  <InfoIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        );
                      })() : (
                        '—'
                      )}
                    </DataTableCell>
                    <DataTableCell>
                      {fn.lastRun ? (
                        <Tooltip title={fn.lastRun.message ?? ''}>
                          <span>
                            {fn.lastRun.status === 'success' ? 'Ok' : 'Error'}
                            {' '}
                            <span style={{ color: '#888' }}>
                              {formatRelativeTime(fn.lastRun.timestamp)}
                            </span>
                          </span>
                        </Tooltip>
                      ) : (
                        <span style={{ color: '#888' }}>Never</span>
                      )}
                    </DataTableCell>
                    <DataTableCell>
                      <Switch
                        checked={fn.isEnabled}
                        onChange={() => handleToggleEnabled(fn)}
                        size="small"
                      />
                    </DataTableCell>
                    <DataTableCell align="right">
                      <Tooltip title="Run now">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleRun(fn)}
                            disabled={runningId === fn.id}
                          >
                            {runningId === fn.id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <PlayArrowIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(fn)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => setDeleteConfirmId(fn.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </DataTableCell>
                  </DataTableRow>
                ))
              )}
            </tbody>
          </DataTable>
        </DataTableCard>
      )}
      </Box>

      <Dialog
        open={deleteConfirmId !== null}
        onClose={isDeleting ? undefined : () => setDeleteConfirmId(null)}
      >
        <DialogTitle>Delete Scheduled Function</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete &quot;{deleteTarget?.name}&quot;? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <ScheduledFunctionDialog
        open={dialogOpen}
        scheduledFunction={editingFunction}
        processes={processes}
        isSaving={isSaving}
        error={dialogError}
        onClose={() => {
          if (!isSaving) {
            setDialogOpen(false);
            setEditingFunction(null);
            setDialogError(null);
          }
        }}
        onSave={handleSave}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
